/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { showError } from "@/lib/errors";
import { toast } from "sonner";
import {
  Users, Building2, Crown, Link2, Send, Trash2,
  CheckCircle2, XCircle, Clock3, AlertCircle, RotateCcw, Search,
} from "lucide-react";

/* ------------------------------------------------------------------------- */
/* Constantes & helpers                                                       */
/* ------------------------------------------------------------------------- */
const KIND_META = {
  athlete:    { label: "Athlète",     Icon: Users,     col: "athletes",    role: null,                    route: "/athletes" },
  coach:      { label: "Coach",       Icon: Users,     col: "coaches",     role: "athlete_coached_by",    route: "/coachs" },
  club:       { label: "Club",        Icon: Building2, col: "clubs",       role: "athlete_of_club",       route: "/clubs" },
  federation: { label: "Fédération",  Icon: Crown,     col: "federations", role: "athlete_of_federation", route: "/federations" },
};

// (sourceType, targetType) -> role canonique
const ROLE_MATRIX = {
  athlete: {
    coach:      "athlete_coached_by",
    club:       "athlete_of_club",
    federation: "athlete_of_federation",
  },
  coach: {
    club: "coach_of_club",
  },
  club: {
    federation: "club_of_federation",
  },
  // federation: leaf, pas de target
};

const SOURCE_TITLE = {
  athlete:    { title: "Liens validés (officiels)",        helper: "Déclare un coach, un club ou une fédération existant·e dans l'annuaire." },
  coach:      { title: "Rattachement à un club",           helper: "Déclare le club avec lequel tu travailles. Le lien devient visible après validation par le club." },
  club:       { title: "Rattachement à une fédération",    helper: "Déclare la fédération de tutelle. Le lien devient visible après validation par la fédération." },
  federation: { title: "Liens",                             helper: "" },
};

const STATUS_META = {
  declared: { label: "Brouillon",     variant: "outline",  Icon: AlertCircle, helper: "Visible uniquement par toi. Soumets pour validation." },
  pending:  { label: "En attente",    variant: "secondary", Icon: Clock3,      helper: "Demande envoyée. En attente de validation par l'entité cible." },
  accepted: { label: "Validé",        variant: "default",  Icon: CheckCircle2, helper: "Lien officiel — visible publiquement." },
  rejected: { label: "Refusé",        variant: "destructive", Icon: XCircle,   helper: "Refusé par l'entité cible." },
  revoked:  { label: "Révoqué",       variant: "outline",  Icon: XCircle,     helper: "Lien retiré." },
};

const fmtPeriod = (s, e) => {
  if (!s && !e) return null;
  if (s && e) return `${s} – ${e}`;
  if (s) return `Depuis ${s}`;
  return `Jusqu'en ${e}`;
};

/* ------------------------------------------------------------------------- */
/* Cherche une entité cible existante                                        */
/* ------------------------------------------------------------------------- */
function TargetSearch({ kind, value, onSelect }) {
  const meta = KIND_META[kind];
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setItems([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        // V9.12 — Endpoint unifié /entity-links/targets (regex échappé, public_filter, bypass owner)
        const { data } = await api.get(
          `/entity-links/targets?kind=${encodeURIComponent(kind)}&q=${encodeURIComponent(q)}&limit=10`
        );
        if (!cancelled) setItems(data?.items || []);
      } catch {
        // Fallback legacy sur /{col}?q= si l'endpoint n'est pas dispo
        try {
          const { data } = await api.get(`/${meta.col}?q=${encodeURIComponent(q)}&limit=10`);
          if (!cancelled) setItems(data?.items || []);
        } catch { /* ignore */ }
      }
      finally { if (!cancelled) setLoading(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, kind, meta.col]);

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 p-2.5 rounded-lg border bg-muted/30">
        <div className="flex items-center gap-2 min-w-0">
          <meta.Icon className="h-4 w-4 text-[hsl(var(--aito-lagoon))] shrink-0" />
          <span className="text-sm font-medium truncate">{value.name || value.full_name}</span>
          {value.discipline && <Badge variant="outline" className="text-[10px]">{value.discipline}</Badge>}
        </div>
        <Button size="sm" variant="ghost" onClick={() => onSelect(null)} data-testid="entity-target-clear">Changer</Button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid={`entity-target-search-${kind}`}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={`Rechercher ${meta.label.toLowerCase()}…`}
          className="pl-8"
        />
      </div>
      {open && q.length >= 2 && (
        <div className="absolute z-20 mt-1 w-full rounded-md border bg-popover shadow-md max-h-64 overflow-auto">
          {loading && <div className="px-3 py-2 text-xs text-muted-foreground">Recherche…</div>}
          {!loading && items.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">Aucun résultat. Crée d'abord la fiche {meta.label} dans son annuaire.</div>
          )}
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => { onSelect(it); setQ(""); setOpen(false); }}
              data-testid={`entity-target-option-${kind}-${it.id}`}
              className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex items-center justify-between gap-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <meta.Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate">{it.name || it.full_name}</span>
                {it.coach_candidate && (
                  <Badge variant="outline" className="text-[9px] shrink-0 border-amber-500 text-amber-700">
                    à confirmer Coach
                  </Badge>
                )}
              </div>
              {it.discipline && <span className="text-[10px] text-muted-foreground">{it.discipline}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Bouton d'action sur un lien                                                */
/* ------------------------------------------------------------------------- */
function LinkActions({ link, onChange }) {
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/entity-links/${link.id}/submit`);
      toast.success("Demande envoyée à validation.");
      onChange?.(data);
    } catch (e) {
      showError(e, { fallback: "Échec de la soumission" });
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Retirer ce lien ?")) return;
    setBusy(true);
    try {
      await api.delete(`/entity-links/${link.id}`);
      toast.success("Lien retiré.");
      onChange?.({ ...link, _deleted: true });
    } catch (e) {
      showError(e, { fallback: "Échec" });
    } finally { setBusy(false); }
  };

  const status = link.status;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {status === "declared" && (
        <Button size="sm" variant="default" disabled={busy} onClick={submit} data-testid={`link-submit-${link.id}`}>
          <Send className="h-3.5 w-3.5 mr-1" />Soumettre
        </Button>
      )}
      <Button size="sm" variant="ghost" disabled={busy} onClick={remove} data-testid={`link-delete-${link.id}`}>
        <Trash2 className="h-3.5 w-3.5 mr-1" />{status === "accepted" ? "Révoquer" : "Retirer"}
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Carte d'un lien                                                            */
/* ------------------------------------------------------------------------- */
function LinkCard({ link, onChange, owned }) {
  const kindMeta = KIND_META[link.target_type] || KIND_META.club;
  const statusMeta = STATUS_META[link.status] || STATUS_META.declared;
  const period = fmtPeriod(link.period_start, link.period_end);

  const Icon = kindMeta.Icon;
  const StatusIcon = statusMeta.Icon;

  return (
    <div data-testid={`link-card-${link.id}`} className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        <div className="h-9 w-9 rounded-lg bg-[hsl(var(--aito-lagoon)/0.08)] flex items-center justify-center shrink-0">
          <Icon className="h-4.5 w-4.5 text-[hsl(var(--aito-lagoon))]" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-semibold text-sm truncate" data-testid={`link-target-${link.id}`}>
              {link.target_name_snapshot || "Entité"}
            </span>
            <Badge variant="outline" className="text-[10px] capitalize">{kindMeta.label}</Badge>
            <Badge variant={statusMeta.variant} className="text-[10px] gap-1">
              <StatusIcon className="h-3 w-3" />
              {statusMeta.label}
            </Badge>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
            {period && <span>{period}</span>}
            {link.evidence_url && (
              <a href={link.evidence_url} target="_blank" rel="noreferrer" className="underline truncate">preuve</a>
            )}
            {link.review_note && link.status === "rejected" && (
              <span className="italic">Motif : {link.review_note}</span>
            )}
          </div>
        </div>
      </div>
      {owned && <LinkActions link={link} onChange={onChange} />}
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Formulaire de déclaration                                                  */
/* ------------------------------------------------------------------------- */
function DeclareForm({ sourceType, sourceId, allowedTargets, onCreated }) {
  const targets = allowedTargets.length > 0 ? allowedTargets : Object.keys(ROLE_MATRIX[sourceType] || {});
  const [kind, setKind] = useState(targets[0] || "club");
  const [target, setTarget] = useState(null);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [submitNow, setSubmitNow] = useState(true);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setTarget(null); setPeriodStart(""); setPeriodEnd(""); setEvidenceUrl(""); setSubmitNow(true);
  };

  const create = async () => {
    if (!target) { toast.error("Sélectionne une cible avant de déclarer."); return; }
    const role = ROLE_MATRIX[sourceType]?.[kind];
    if (!role) { toast.error(`Type de lien non supporté : ${sourceType} -> ${kind}`); return; }
    setBusy(true);
    try {
      // V9.22 — Si la cible est un Athlete candidat Coach, on promeut d'abord
      let effectiveTargetId = target.id;
      if (kind === "coach" && target.coach_candidate) {
        try {
          const promoteBody = target.candidate_user_id
            ? { user_id: target.candidate_user_id }
            : { athlete_id: target.id };
          const { data: promoted } = await api.post("/entity-links/promote-coach", promoteBody);
          effectiveTargetId = promoted.coach_id || target.id;
          if (promoted.promoted) {
            toast.info(`${target.full_name || target.name} a été promu·e Coach. La demande d'affiliation sera ensuite validée par cette personne.`);
          }
        } catch (e) {
          toast.error("Impossible de promouvoir cette personne comme Coach.");
          setBusy(false);
          return;
        }
      }

      const { data } = await api.post("/entity-links", {
        source_type: sourceType,
        source_id: sourceId,
        target_type: kind,
        target_id: effectiveTargetId,
        role,
        period_start: periodStart ? parseInt(periodStart, 10) : null,
        period_end: periodEnd ? parseInt(periodEnd, 10) : null,
        evidence_url: evidenceUrl || null,
        submit: submitNow,
      });
      if (data?.already_exists) {
        toast.info(`Un lien actif existe déjà pour ${target.name || target.full_name}.`);
      } else {
        toast.success(submitNow ? "Lien soumis à validation." : "Brouillon enregistré.");
      }
      onCreated?.(data.item);
      reset();
    } catch (e) {
      showError(e, { fallback: "Échec de la déclaration" });
    } finally { setBusy(false); }
  };

  return (
    <div className="border-dashed border-2 border-border/60 rounded-lg p-3 space-y-3 bg-muted/20">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
        <Link2 className="h-3.5 w-3.5" />Déclarer un nouveau lien
      </div>

      {targets.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {targets.map((k) => {
            const m = KIND_META[k]; if (!m) return null;
            return (
              <button
                key={k}
                type="button"
                onClick={() => { setKind(k); setTarget(null); }}
                data-testid={`entity-kind-${k}`}
                className={`text-xs px-2.5 py-1.5 rounded-md border flex items-center gap-1.5 transition-colors ${
                  kind === k ? "bg-[hsl(var(--aito-lagoon))] text-white border-transparent"
                            : "hover:bg-accent border-border"
                }`}
              >
                <m.Icon className="h-3.5 w-3.5" />{m.label}
              </button>
            );
          })}
        </div>
      )}

      <TargetSearch kind={kind} value={target} onSelect={setTarget} />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div>
          <label className="text-[11px] text-muted-foreground">Année début</label>
          <Input
            data-testid="entity-period-start"
            type="number" inputMode="numeric"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
            placeholder="2018"
          />
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground">Année fin (vide = actuel)</label>
          <Input
            data-testid="entity-period-end"
            type="number" inputMode="numeric"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
            placeholder=""
          />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <label className="text-[11px] text-muted-foreground">URL preuve (optionnel)</label>
          <Input
            data-testid="entity-evidence"
            value={evidenceUrl}
            onChange={(e) => setEvidenceUrl(e.target.value)}
            placeholder="https://…"
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <Checkbox
            data-testid="entity-submit-now"
            checked={submitNow}
            onCheckedChange={(v) => setSubmitNow(!!v)}
          />
          Soumettre immédiatement à validation
        </label>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={reset} disabled={busy} data-testid="entity-form-reset">
            <RotateCcw className="h-3.5 w-3.5 mr-1" />Réinitialiser
          </Button>
          <Button size="sm" onClick={create} disabled={busy || !target} data-testid="entity-form-declare">
            <Link2 className="h-3.5 w-3.5 mr-1" />Déclarer
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Section "Liens validés" — pour la page d'édition (mode propriétaire)      */
/* ------------------------------------------------------------------------- */
export function EntityLinksManager({ athleteId, sourceType = "athlete", sourceId, allowedTargets = [] }) {
  // Rétro-compat : si athleteId est passé, on l'utilise comme sourceId/athlete
  const effectiveSourceId = sourceId || athleteId;
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active"); // active = declared+pending+accepted

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/entity-links?source_type=${sourceType}&source_id=${effectiveSourceId}&limit=200`);
      setLinks(data?.items || []);
    } catch (e) {
      toast.error("Impossible de charger les liens.");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (effectiveSourceId) load(); /* eslint-disable-next-line */ }, [effectiveSourceId, sourceType]);

  const visibleLinks = useMemo(() => {
    if (filter === "all") return links;
    if (filter === "active") return links.filter((l) => ["declared", "pending", "accepted"].includes(l.status));
    return links.filter((l) => l.status === filter);
  }, [links, filter]);

  const counts = useMemo(() => {
    const c = { declared: 0, pending: 0, accepted: 0, rejected: 0, revoked: 0 };
    links.forEach((l) => { if (c[l.status] != null) c[l.status]++; });
    return c;
  }, [links]);

  const onChange = (updated) => {
    if (!updated) return;
    if (updated._deleted) {
      setLinks((prev) => prev.filter((l) => l.id !== updated.id));
      return;
    }
    setLinks((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
  };
  const onCreated = (item) => { if (item) setLinks((prev) => [item, ...prev]); };

  return (
    <div className="space-y-3" data-testid="entity-links-manager">
      <div className="flex items-center gap-1.5 flex-wrap">
        {[
          ["active",   `Actifs (${counts.declared + counts.pending + counts.accepted})`],
          ["declared", `Brouillons (${counts.declared})`],
          ["pending",  `En attente (${counts.pending})`],
          ["accepted", `Validés (${counts.accepted})`],
          ["rejected", `Refusés (${counts.rejected})`],
          ["all",      "Tous"],
        ].map(([k, lbl]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`entity-filter-${k}`}
            className={`text-[11px] px-2 py-1 rounded-md border transition-colors ${
              filter === k ? "bg-foreground text-background border-transparent" : "hover:bg-accent"
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Chargement…</div>
      ) : visibleLinks.length === 0 ? (
        <div className="text-sm text-muted-foreground italic px-3 py-4 border rounded-lg bg-muted/20">
          Aucun lien dans cette catégorie. Utilise le formulaire ci-dessous pour déclarer un coach, un club ou une fédération.
        </div>
      ) : (
        <div className="space-y-2">
          {visibleLinks.map((l) => (
            <LinkCard key={l.id} link={l} owned onChange={onChange} />
          ))}
        </div>
      )}

      <DeclareForm sourceType={sourceType} sourceId={effectiveSourceId} allowedTargets={allowedTargets} onCreated={onCreated} />

      <p className="text-[11px] text-muted-foreground italic">
        {SOURCE_TITLE[sourceType]?.helper || "Les liens deviennent visibles publiquement uniquement après validation par l'entité cible (ou par la COPF). Les brouillons et demandes en attente restent privés."}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Section publique — n'affiche que les liens accepted                        */
/* ------------------------------------------------------------------------- */
export function PublicEntityLinks({ athleteId, sourceType = "athlete", sourceId, fallbackLegacy }) {
  const effectiveSourceId = sourceId || athleteId;
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!effectiveSourceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/entity-links?source_type=${sourceType}&source_id=${effectiveSourceId}&status=accepted&limit=200`);
        if (!cancelled) setLinks(data?.items || []);
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [effectiveSourceId, sourceType]);

  if (loading) return null;

  // Si aucun lien validé, on retombe sur l'affichage legacy (chaînes) si fourni
  if (links.length === 0) return fallbackLegacy || null;

  const groupBy = links.reduce((acc, l) => {
    (acc[l.target_type] ||= []).push(l);
    return acc;
  }, {});

  return (
    <div data-testid="public-entity-links" className="space-y-3">
      {["coach", "club", "federation"].map((kind) => {
        const list = groupBy[kind] || [];
        if (list.length === 0) return null;
        const m = KIND_META[kind];
        return (
          <div key={kind}>
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
              <m.Icon className="h-3.5 w-3.5" />
              <span>{kind === "coach" ? "Coachs validés" : kind === "club" ? "Clubs validés" : "Fédérations validées"}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {list.map((l) => {
                const period = fmtPeriod(l.period_start, l.period_end);
                const targetSlug = l.target_slug || l.target_id;
                return (
                  <Link
                    key={l.id}
                    to={`${m.route}/${targetSlug}`}
                    data-testid={`public-link-${l.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium py-1.5 pl-2.5 pr-3 rounded-full border border-[hsl(var(--aito-lagoon)/0.3)] bg-[hsl(var(--aito-lagoon)/0.06)] hover:bg-[hsl(var(--aito-lagoon)/0.12)] transition-colors"
                  >
                    <CheckCircle2 className="h-3 w-3 text-[hsl(var(--aito-lagoon))]" />
                    <span>{l.target_name_snapshot || "Entité"}</span>
                    {period && <span className="text-muted-foreground">· {period}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
